<script lang="ts" setup>
import RothcoTable from '@/components/RothcoTable.vue'
import { computed, ref, watch, inject, onMounted } from 'vue'
import { useFetch } from '@/helpers/composables/useFetch';
import { useDialog } from "primevue/usedialog";
import DeleteUser from "@/components/DeleteUserModal.vue";
import UserToggleActiveState from "@/components/UserToggleActiveStateModal.vue";
import { router, usePage } from '@inertiajs/vue3';
import RothcoCta from '@/components/RothcoCta.vue';
import AddUserModal from "@/components/AddUserModal.vue";
import UpdateUserModal from "@/components/UpdateUserModal.vue";
import CustomSelect from '@/components/CustomSelect.vue';
import { useRouter } from '@/helpers/composables/useRouter';
import {useSettingsStore} from '@/store/settings';
import AccountActiveIcon from "@icons/account-icon-active.svg";
import AccountIcon from "@icons/account-icon.svg";
import PencilIcon from "@icons/pencil.svg";
import RemoveIcon from "@icons/remove-icon.svg";
const dialog = useDialog();
let route = inject('route');
let props = defineProps({
   users: {
         type: Object,
         required: true,
         default() {
             return {
                 data: [],
                 meta: {
                     total: 0
                 },
                  links: {
                     first: null,
                     last: null,
                     prev: null,
                     next: null
                  }
             }
         }
   }
});
let editingRow = ref(null);

let users = ref(props.users.data);


let tableData = computed({
   get(){
         return users.value.map((user)=>{
            return {
                  data: user,
            }
         });
   },
   set(value){
         users.value = value.map((user)=>{
            return user.data;
         });
   }
});

let backendPagination = computed(()=>{
    return {
        ...props.users.meta,
        ...props.users.links
    }
});

let visibleFields = computed(()=>{
    return [
        {
         "label": "User",
         "field": 'username',
         pt: {
            bodyCell: {
               class: 'mob-w-100', 'data-label': 'User'
            }
         }
        },
        {
         "label": "Email",
         "field": 'email',
         show_only: 'desktop',
         pt: {
            bodyCell: {
               class: 'mob-w-100', 'data-label': 'Email'
            }
         }
        },
        {
         "label": "Account Level",
         "field": 'account_level_name',
         pt: {
            bodyCell: {
               class: 'mob-w-100', 'data-label': 'Account Level'
            }
         }
        },
        {
         "label": "Product Emails",
         "field": 'product_emails',
         "is_custom": true,
         pt: {
            bodyCell: {
               class: 'mob-w-50 text-center', 'data-label': 'Product Emails'
            }
         }
        },
        {
         "label": "News Alerts",
         "field": 'news_emails',
         "is_custom": true,
         pt: {
            bodyCell: {
               class: 'mob-w-50 text-center', 'data-label': 'News Emails'
            }
         }
        },
        {
         "label": "Item Feed",
         "field": 'feed_emails',
         "is_custom": true,
         pt: {
            bodyCell: {
               class: 'mob-w-50 text-center', 'data-label': 'Feed Updates'
            }
         }
        },
        {
         "label": "2FA Auth.",
         "field": 'fa_enabled',
         "is_custom": true,
         pt: {
            bodyCell: {
               class: 'mob-w-50 text-center', 'data-label': '2FA Auth.'
            }
         }
        },
        {
         "label": "Actions",
         "field": 'actions',
         "is_custom": true,
         pt: {
            bodyCell: {
               class: 'mob-w-100 text-center user-actions', 'data-label': 'Actions'
            }
         }
        }
    ]
});

let updateUser = computed(()=>{
   if(!editingRow.value) return ()=>{};
   let { execute: update, } = useFetch(route('endpoint.user.update', editingRow.value.user_id), {
      immediate: false,
      beforeFetch: (ctx)=>{
         ctx.options.headers = {
            ...ctx.options.headers,
            'Accept': 'application/json',
         }
         return ctx;
      },
      afterFetch: (ctx)=>{
         if(ctx.response.ok){
            editingRow.value = null;
            const user = ctx.data;
            setNotificationMessage({
               message: `User ${user.username} updated successfully`,
               type: 'success'
            })
         }
         router.reload()
      },
      onFetchError: (ctx)=>{
         if(ctx.data.message){
            setNotificationMessage({
               message: ctx.data.message.message,
               type: ctx.data.message.type
            })
         }
         router.reload()
      }
   }).put(
      {
         user: editingRow.value
      }
   ).json();

   return update;
})

let userToDelete = ref(null);


let deleteUser = computed(()=>{
   if(!userToDelete.value) return (user)=>{
      return;
   }
   let { execute: deleteFn, } = useFetch(route('endpoint.user.destroy', userToDelete.value?.user_id), {
      immediate: false,
      beforeFetch: (ctx)=>{
         ctx.options.headers = {
            ...ctx.options.headers,
            'Accept': 'application/json',
         }
         return ctx;
      },
      afterFetch: (ctx)=>{
         if(ctx.response.ok){
            const user = ctx.data;
            setNotificationMessage({
               message: `User ${user.username} deleted successfully`,
               type: 'success'
            })
         }
         router.reload()
      },
      onFetchError: (ctx)=>{
         if(ctx.data.message){
            setNotificationMessage({
               message: ctx.data.message.message,
               type: ctx.data.message.type
            })
         }
         router.reload()
      }
   }).delete().json();

   return deleteFn;
})

let toggleUserActiveState = (user) => {
   user.is_active = user.is_active == 1 ? 0 : 1;
   editingRow.value = user;
   updateUser.value();
}

let handleUserUpdate = (field, event, user) => {
   editingRow.value = {
      ...user,
      [field]: event.target.checked ? 1 : 0
   };
   updateUser.value();
}

let confirmToggleUserActiveState = (user)=>{
   dialog.open(UserToggleActiveState, {
      props: {
         showHeader: false,
         modal: true,
      },
      data: {
         user
      },
      onClose: (event)=>{
         if(event.data?.submit){
            toggleUserActiveState(user);
         }
      }
   })
}

let confirmDeleteUser = (user)=>{
   userToDelete.value = user;

   dialog.open(DeleteUser, {
      props: {
         showHeader: false,
         modal: true,
      },
      data: {
         user
      },
      onClose: (event)=>{
         if(event.data?.deleteUser){
            deleteUser.value();
         }else{
            userToDelete.value = null;
         }
      }
   })
}


let addUser = ()=>{
   dialog.open(AddUserModal, {
      props: {
         showHeader: false,
         modal: true,
      },
      onClose: (event)=>{
         if(event.data?.addedUser){
            router.reload();
         }
      }
   })
}


watch(()=>props.users.data, (newVal)=>{
   users.value = newVal;
});

let editUser = (user)=>{
   dialog.open(UpdateUserModal, {
      props: {
         showHeader: false,
         modal: true,
      },
      data: {
         user
      },
      onClose: (event)=>{
         if(event.data?.updatedUser){
            router.reload();
         }
      }
   })
}

let searchUserName = ref('');


let permissionFilters = [
   {
      value: 'All',
      key: '0',
      id: '0'
   },
   {
      value: 'Administrator',
      key: '1',
      id: '1'
   },
   {
      value: "Can See Pricing",
      key: '2',
      id: '2'
   },
   {
      value: "Places Orders",
      key: '3',
      id: '3'
   },
   {
      value: "Basic Display Only",
      key: '4',
      id: '4'
   }
]

let activeStatuses = [
   {
      value: 'All',
      key: "null",
      id: 'null'
   },
   {
      value: 'Active',
      key: '1',
      id: '1'
   },
   {
      value: "Inactive",
      key: '0',
      id: '0'
   }
]

let accountLevel = ref();
let activeStatus = ref();

let urlParams = computed(()=>{
   let params = {
      filter: {}
   };

   if(accountLevel.value){
      params.filter['account_level'] = accountLevel.value.key == '0' ? undefined : accountLevel.value.key;
   }

   if(activeStatus.value){
      params.filter['is_active'] = activeStatus.value.key == 'null' ? undefined : activeStatus.value.key;
   }

   if(searchUserName.value){
      params.filter['search'] = searchUserName.value;
   }
   return params;
})

let { to } = useRouter({
   router,
   route
});

onMounted(()=>{
   let urlParams = new URLSearchParams(window.location.search);
   let urlAccountLevel = urlParams.get('filter[account_level]');
   let urlActiveStatus = urlParams.get('filter[is_active]');
   let urlSearch = urlParams.get('filter[search]');

   if(urlAccountLevel){
      accountLevel.value = permissionFilters.find((filter)=>filter.key == urlAccountLevel);
   }else{
      accountLevel.value = permissionFilters[0];
   }

   if(urlActiveStatus){
      activeStatus.value = activeStatuses.find((filter)=>filter.key == urlActiveStatus);
   }else{
      activeStatus.value = activeStatuses[0];
   }

   if(urlSearch){
      searchUserName.value = urlSearch;
   }

});

function searchUsers(){
   to({
      newParams: {
         ...urlParams.value,
         page: 1
      },
      options: {preserveState: true, preserveScroll: true}
   })
}

let { setNotificationMessage } = useSettingsStore();
</script>
<template>
 <div class="user-accounts account-page-padding">
    <h1 class="headline-xl">User Accounts</h1>
      <p class="paragraph-feature">Manage all of your user accounts</p>

      <form>
         <div class="search-fields" style="padding-top: 2rem;">
            <div>
               <custom-select id="account_level_filter" label="Filter by Account Level" v-model="accountLevel" :options="permissionFilters"/>
            </div>
         </div>
         <div class="search-fields">
            <div>
               <custom-select id="active_status_filter" label="Active Status" v-model="activeStatus" :options="activeStatuses"/>
            </div>
            <div class="field-state input-folder">
               <div class="input-folder--input">
                     <PrimeInputText
                        v-model="searchUserName"
                        type="search" 
                        id="search_input"
                        ref="search_input" 
                        placeholder=" "
                        maxlength="100"
                     />
               </div>
               <label for="search_input">
                     <p class="paragraph-default">User Search</p>
               </label>
            </div>
         </div>
         <div class="search-fields">
            
            <div class="buttons">
               <RothcoCta
               id="search_button"
               ref="search_button" 
               type="submit"
               :inertia="false"
               @click.prevent="searchUsers">Search Users</RothcoCta>
               <RothcoCta
               id="search_button"
               ref="search_button" 
               type="button"
               :inertia="false"
               @click.prevent="addUser">Add User</RothcoCta>
            </div>
         </div>
      </form>
      <RothcoTable style="overflow-x:scroll" key="table" dataKey="user_id" :content="tableData" :visible-fields="visibleFields" :backendPagination="backendPagination" use-backend-sorting>
         <template v-for="(item, index) in tableData" :key="`product_emails_${index}`" v-slot:[`product_emails_${index}`]="slotProps">
            <input :id="`product_emails_${index}`" :true-value="1" :false-value="0" type="checkbox" v-model="item.data.product_emails" @input="handleUserUpdate('product_emails', $event, item.data)">
         </template>

         <template v-for="(item, index) in tableData" :key="`news_emails_${index}`" v-slot:[`news_emails_${index}`]="slotProps">
            <input :id="`news_emails_${index}`" :true-value="1" :false-value="0" type="checkbox" v-model="item.data.news_emails" @input="handleUserUpdate('news_emails', $event, item.data)" />
         </template>

         <template v-for="(item, index) in tableData" :key="`feed_emails_${index}`" v-slot:[`feed_emails_${index}`]="slotProps">
            <input :id="`feed_emails_${index}`" :true-value="1" :false-value="0" type="checkbox" v-model="item.data.feed_emails" @input="handleUserUpdate('feed_emails', $event, item.data)" />
         </template>

         <template v-for="(item, index) in tableData" :key="`fa_enabled_${index}`" v-slot:[`fa_enabled_${index}`]="slotProps">
               <input :id="`fa_enabled_${index}`" :true-value="1" :false-value="0" type="checkbox" v-model="item.data.fa_enabled" @input="handleUserUpdate('fa_enabled', $event, item.data)" />
         </template>

         <template v-for="(item, index) in tableData" :key="`actions_${index}`" v-slot:[`actions_${index}`]="slotProps">
            <div class="flex-row-forced" v-if="usePage().props?.user?.user_id != item.data.user_id">
               <AccountActiveIcon :id="`deactivate_user_${index}`" @click="confirmToggleUserActiveState(item.data)" v-if="item.data.is_active == 1" class="icon" alt="Deactivate user" aria-label="Deactivate user" title="Deactivate User" />
               <AccountIcon :id="`activate_user_${index}`" @click="confirmToggleUserActiveState(item.data)" v-else class="icon" alt="Activate user" aria-label="Activate user" title="Activate User" />
               <PencilIcon class="icon" alt="Edit user" aria-label="Edit user" @click="editUser(item.data)" />
               <RemoveIcon @click="confirmDeleteUser(item.data)" class="icon" :alt="`Remove user ${item.data.username}`" aria-label="Remove user" />
            </div>
            <div class="flex-row-forced" v-else>
               <PencilIcon class="icon" alt="Edit user" aria-label="Edit user" @click="editUser(item.data)" />
            </div>
         </template>
      </RothcoTable>
 </div>
</template>